import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Location,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { PageRoute } from "@/constants/pages";
import { SignInAction } from "@/data/models/SignInAction";
import { getCookie } from "@/domain/getCookie";
import { useBookAppointmentMutation } from "@/hooks/booking";
import { useUserQuery } from "@/hooks/user";
import { fetchUser, SignInResponse } from "@/services/user";
import { isSet, isSetAndNotEmpty } from "@/utils/helpers";

import { StandardContent } from "../App";

const MISSING_REQUIRED_FIELDS_KEY = "missingRequiredFields";

type AuthContextType = {
  guid?: string;
  user?: Awaited<ReturnType<typeof fetchUser>>;
  userQuery: ReturnType<typeof useUserQuery>;
  isSignedIn: boolean;
  isLoading: boolean;
  logout: () => void;
  signInWithToken: (params: {
    res: SignInResponse;
    action: SignInAction | null;
  }) => void;
  missingRequiredFields?: boolean;
  hasIncompletedAccountInformation: boolean;
  waiver: {
    state: WaiverState;
    expiresAt?: Date;
  };
  onCompletedRequiredFields: () => void;
};

const AuthContext = createContext({} as AuthContextType);

type WaiverState = "VALID" | "EXPIRED" | "NOT_SIGNED";

function isStaffOneTimeCodePath(pathname: string) {
  return pathname.startsWith(PageRoute.STAFF_CLIENT_ONE_TIME_CODE);
}

function isSigninCodePath(pathname: string) {
  return pathname.startsWith(PageRoute.AUTH_SIGN_IN_CODE);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotification();
  const [guid, setGuid] = useState<string | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>(getCookie("token"));
  const [missingRequiredFields, setMissingRequiredFields] = useState<
    boolean | undefined
  >(localStorage.getItem(MISSING_REQUIRED_FIELDS_KEY) === "true");
  const bookAppointmentMutation = useBookAppointmentMutation();

  useEffect(() => {
    if (!token) {
      setGuid(undefined);
      Sentry.setUser(null);
    }
  }, [token]);

  const handleLogout = useCallback(() => {
    setToken(undefined);
    setMissingRequiredFields(undefined);
    Cookies.remove("token");
    queryClient.clear();
  }, [queryClient]);

  const userQuery = useUserQuery(token);

  useEffect(() => {
    if (userQuery.isError) {
      handleLogout();
    }
  }, [userQuery.isError, handleLogout]);

  useEffect(() => {
    if (userQuery.isSuccess) {
      Sentry.setUser({ email: userQuery.data.email });
      setGuid(userQuery.data.guid);
    }
  }, [userQuery.isSuccess, userQuery.data]);

  const waiverExpiresAt = userQuery.data?.core_waiver_expires
    ? new Date(userQuery.data.core_waiver_expires)
    : undefined;

  const waiverState = useMemo<WaiverState>(() => {
    if (userQuery.data?.has_valid_core_waiver) {
      return "VALID";
    } else {
      if (userQuery.data?.core_waiver_expires) {
        return "EXPIRED";
      } else {
        return "NOT_SIGNED";
      }
    }
  }, [userQuery.data]);

  const hasIncompletedAccountInformation = useMemo(() => {
    if (userQuery.data) {
      return !(
        userQuery.data.first_name &&
        userQuery.data.last_name &&
        userQuery.data.address &&
        userQuery.data.city &&
        userQuery.data.state &&
        userQuery.data.postal_code &&
        userQuery.data.emergency_name &&
        userQuery.data.emergency_phone &&
        userQuery.data.gender &&
        userQuery.data.mobile_phone &&
        userQuery.data.dob
      );
    } else {
      return false;
    }
  }, [userQuery.data]);

  function onGoToBookAppointment(params: {
    storeCode: string;
    service: number;
  }) {
    const { storeCode, service } = params;
    return navigate(`/book?store=${storeCode}&service=${service}`, {
      replace: true,
    });
  }

  function onRedirectOrHome(params: {
    redirect: string | null;
    from: string | null;
  }) {
    const { redirect, from } = params;
    if (redirect) {
      setTimeout(() => {
        window.location.href = redirect;
      }, 500);
    } else {
      navigate(from ? (JSON.parse(from) as Location) : "/", {
        replace: true,
      });
    }
  }

  function onSignInAction(params: {
    action: SignInAction;
    redirect: string | null;
    from: string | null;
  }) {
    const { action, redirect, from } = params;
    if (!action) {
      return;
    }
    switch (action.type) {
      case "BOOK_APPOINTMENT": {
        bookAppointmentMutation.mutate(
          {
            storeCode: action.storeCode,
            serviceId: action.service.toString(),
            time: action.time,
            notes: "",
          },
          {
            onSuccess: (bookAppointmentResult) => {
              switch (bookAppointmentResult.type) {
                case "success": {
                  notify("Appointment Successfully Booked", "success");
                  onRedirectOrHome({ redirect, from });
                  break;
                }
                case "noSlotsAvailable": {
                  notify(
                    "Appointment unavailable, please select a new time",
                    "error",
                  );
                  return onGoToBookAppointment({
                    storeCode: bookAppointmentResult.params.storeCode,
                    service: bookAppointmentResult.params.serviceId,
                  });
                }
                case "error": {
                  notify(
                    "There was a problem booking your appointment",
                    "error",
                  );
                  onGoToBookAppointment({
                    storeCode: bookAppointmentResult.params.storeCode,
                    service: bookAppointmentResult.params.serviceId,
                  });
                  break;
                }
              }
            },
          },
        );
        break;
      }
      case "WAIVER": {
        navigate(`/waiver`, {
          replace: true,
        });
        break;
      }
      default: {
        break;
      }
    }
  }

  function signInWithToken(params: {
    res: SignInResponse;
    action: SignInAction | null;
  }) {
    const { res, action } = params;
    const { token, missingRequiredFields } = res;

    queryClient.clear();

    setMissingRequiredFields(missingRequiredFields);
    setToken(token);

    Cookies.set("token", token, {
      expires: 182,
      secure: true,
    });

    //there seems to be an issue on IOS, namely IPAD where setting the cookie and then redirecting
    //doesn't actually set the cookie. Attempting to set and then read
    getCookie("token");

    if (missingRequiredFields) {
      localStorage.setItem(
        MISSING_REQUIRED_FIELDS_KEY,
        missingRequiredFields.toString(),
      );
      navigate("/auth/required-info", { replace: true });
      return;
    }

    localStorage.removeItem(MISSING_REQUIRED_FIELDS_KEY);

    const from = localStorage.getItem("from");
    const redirect = localStorage.getItem("redirect");

    localStorage.removeItem("from");
    localStorage.removeItem("redirect");

    if (isSet(action)) {
      onSignInAction({ action, redirect, from });
    } else {
      onRedirectOrHome({ redirect, from });
    }
  }

  function onCompletedRequiredFields() {
    const from = localStorage.getItem("from");
    const redirect = localStorage.getItem("redirect");

    localStorage.removeItem("from");
    localStorage.removeItem("redirect");
    localStorage.removeItem(MISSING_REQUIRED_FIELDS_KEY);
    setMissingRequiredFields(false);

    onRedirectOrHome({ redirect, from });
  }

  const value: AuthContextType = {
    isSignedIn: !!token,
    guid: guid,
    isLoading: userQuery.isLoading,
    user: userQuery.data,
    logout: handleLogout,
    waiver: {
      state: waiverState,
      expiresAt: waiverExpiresAt,
    },
    hasIncompletedAccountInformation: hasIncompletedAccountInformation,
    signInWithToken: signInWithToken,
    userQuery: userQuery,
    missingRequiredFields: missingRequiredFields,
    onCompletedRequiredFields: onCompletedRequiredFields,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function RequireAuth() {
  const { isSignedIn, isLoading, missingRequiredFields } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const email = searchParams.get("email");
  const code = searchParams.get("code");

  if (missingRequiredFields) {
    const existingFrom = localStorage.getItem("from");
    if (!isSet(existingFrom)) {
      localStorage.setItem("from", JSON.stringify(location));
    }
    return <Navigate to="/auth/required-info" replace />;
  }

  if (
    isSetAndNotEmpty(code) &&
    !isSigninCodePath(location.pathname) &&
    !isStaffOneTimeCodePath(location.pathname)
  ) {
    const locationWithoutCode = { ...location };
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("code");
    locationWithoutCode.search = "?" + searchParams.toString();
    localStorage.setItem("from", JSON.stringify(locationWithoutCode));
    return (
      <Navigate to={`/auth/sign-in/code?code=${encodeURIComponent(code)}`} />
    );
  }

  if (!isSignedIn) {
    localStorage.setItem("from", JSON.stringify(location));
    if (isSetAndNotEmpty(email)) {
      return (
        <Navigate
          to={`/auth/sign-in?email=${encodeURIComponent(email)}`}
          replace
        />
      );
    }
    if (
      !isStaffOneTimeCodePath(location.pathname) &&
      !isSigninCodePath(location.pathname)
    ) {
      return <Navigate to="/auth" replace />;
    }
  }

  if (isLoading) {
    return (
      <StandardContent>
        <Loader />
      </StandardContent>
    );
  }

  return <Outlet />;
}
